
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { FusionContentYoutube, SpfBucketFileFrontend } from '@shared/index';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectButtonModule } from 'primeng/selectbutton';
import { SubscriptionLike } from 'rxjs';
import { GotoBlocklyDirective } from 'src/app/shared/directives/goto-blockly.directive';
import { PlaceholderInfoDirective } from 'src/app/shared/directives/placeholder-info.directive';
import { EditorItemsService } from 'src/app/shared/services/editor-items.service';
import { StageAdminService } from '@shared-angular/services/stage-admin.service';
import { customCode } from '../../../../../../../../shared/services/codecompletion.service';
import { FusionService } from '@shared-angular/services/fusion.service';
import { LanguageService } from '@shared-angular/services/language.service';
import { RouteShareService } from '../../../../../../../../shared/services/route-share.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { getFusionBotTypeOptions } from '../fusionbot-tab.utils';
import { CodemirrorHtmlComponent } from '../../../../../../../../shared/components/ai-bot/utils/codemirror-html/codemirror-html.component';

@Component({
  selector: 'app-fusion-youtube-config',
  imports: [
    FormsModule,
    GotoBlocklyDirective,
    PlaceholderInfoDirective,
    CodemirrorHtmlComponent,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    SelectModule,
    ToggleSwitchModule,
    RadioButtonModule,
    SelectButtonModule
  ],
  templateUrl: './fusion-youtube-config.component.html',
  styleUrl: './fusion-youtube-config.component.scss'
})
export class FusionYoutubeConfigComponent implements OnInit, OnDestroy {
  public safeHtml: SafeHtml = "";
  public subscriptions: SubscriptionLike[] = [];
  public selectedFusionContent?: SpfBucketFileFrontend<string, FusionContentYoutube>;
  public showHtmlEditor = false;
  protected showAdvanced = false;
  protected get botTypeOptions() {
    return getFusionBotTypeOptions(this.fusionService.loginService.systemConf);
  }
  protected systemLanguageOptions = [
    { label: $localize`:@@fusionconfig-systemlang-de:Deutsch`, value: 'de' },
    { label: $localize`:@@fusionconfig-systemlang-en:Englisch`, value: 'en' },
    { label: $localize`:@@fusionconfig-systemlang-fr:Französisch`, value: 'fr' },
    { label: $localize`:@@fusionconfig-systemlang-es:Spanisch`, value: 'es' },
    { label: $localize`:@@fusionconfig-systemlang-it:Italienisch`, value: 'it' },
    { label: $localize`:@@fusionconfig-systemlang-nl:Niederländisch`, value: 'nl' },
    { label: $localize`:@@fusionconfig-systemlang-nb:Norwegisch`, value: 'nb-no' },
    { label: $localize`:@@fusionconfig-systemlang-fr-be:Französisch (Belgien)`, value: 'fr-be' },
    { label: $localize`:@@fusionconfig-systemlang-nl-be:Niederländisch (Belgien)`, value: 'nl-be' },
    { label: $localize`:@@fusionconfig-systemlang-pl:Polnisch`, value: 'pl' },
    { label: $localize`:@@fusionconfig-systemlang-pt:Portugiesisch`, value: 'pt' },
    { label: $localize`:@@fusionconfig-systemlang-hu:Ungarisch`, value: 'hu' },
    { label: $localize`:@@fusionconfig-systemlang-ro:Rumänisch`, value: 'ro' },
    { label: $localize`:@@fusionconfig-systemlang-tr:Türkisch`, value: 'tr' },
    { label: $localize`:@@fusionconfig-systemlang-fi:Finnisch`, value: 'fi' },
    { label: $localize`:@@fusionconfig-systemlang-sv:Schwedisch`, value: 'sv' },
    { label: $localize`:@@fusionconfig-systemlang-da:Dänisch`, value: 'da' },
    { label: $localize`:@@fusionconfig-systemlang-cs:Tschechisch`, value: 'cs' },
    { label: $localize`:@@fusionconfig-systemlang-el:Griechisch`, value: 'el' }
  ];
  protected viewModeOptions = [
    { label: $localize`:@@fusionconfig-viewmode-split:Split (Side-by-Side)`, value: 'split' },
    { label: $localize`:@@fusionconfig-viewmode-split-reverse:Split Reverse (Side-by-Side)`, value: 'split-reverse' },
    { label: $localize`:@@fusionconfig-viewmode-window:Fenster (Overlay)`, value: 'window' },
    { label: $localize`:@@fusionconfig-viewmode-full:Vollbild`, value: 'full' },
    { label: $localize`:@@fusionconfig-viewmode-float:Floating (Chat-Bubble)`, value: 'float' }
  ];
  protected annotationOptions = [
    { label: $localize`:@@youtube-playervars-annotation-default:Standard`, value: 'default' },
    { label: $localize`:@@youtube-playervars-annotation-on:An`, value: '1' },
    { label: $localize`:@@youtube-playervars-annotation-off:Aus`, value: '3' }
  ];
  protected progressColorOptions = [
    { label: $localize`:@@youtube-playervars-progressbarcolor-red:Rot`, value: 'red' },
    { label: $localize`:@@youtube-playervars-progressbarcolor-white:Weiß`, value: 'white' }
  ];
  protected captionOptions = [
    { label: $localize`:@@youtube-playervars-closed-captions-load-default:Standard`, value: 'default' },
    { label: $localize`:@@youtube-playervars-closed-captions-load-on:Aktiviert`, value: '1' },
    { label: $localize`:@@youtube-playervars-closed-captions-load-off:Deaktiviert`, value: '0' }
  ];

  constructor(public fusionService: FusionService,
    public rss: RouteShareService,
    public langService: LanguageService,
    public sanitizer: DomSanitizer,
    public toastr: ToastrService,
    protected stageAdmin: StageAdminService,
    protected editorItemService: EditorItemsService
  ) { }

  public ngOnDestroy(): void {
    this.subscriptions.forEach(s => { s.unsubscribe(); });
  }

  public ngOnInit(): void {
    this.selectedFusionContent = this.fusionService.selectedFusionContent as SpfBucketFileFrontend<string, FusionContentYoutube>;
    this.subscriptions.push(this.fusionService.onFusionConfigChanged.subscribe(() => {
      this.updateSafeUrlString();
    }));
    this.subscriptions.push(this.fusionService.onFusionConfigDeleted.subscribe(() => {
      this.updateSafeUrlString();
    }));
    this.subscriptions.push(this.fusionService.onFusionConfigCreated.subscribe(() => {
      this.updateSafeUrlString();
    }));
    this.updateSafeUrlString();
  }

  public async deleteYoutube() {
    await this.fusionService.deleteFile(this.rss.botConfig._id, "youtube");
    await this.fusionService.loadFusionInfo(this.rss.botConfig._id);
  }
  // function
  public getYoutubeUrlId(url: string) {
    const urlObject = new URL(url);
    const urlOrigin = urlObject.origin;
    const urlPath = urlObject.pathname;
    if (urlOrigin.search('youtu.be') > -1) {
      return urlPath.substring(1);
    }
    if (urlPath.search('embed') > -1) {
      return urlPath.substring(7);
    }
    return urlObject.searchParams.get('v');
  }

  public zeroOneSwitch(inp: number) {
    if (inp == 0) {
      return 1;
    }
    return 0;
  }

  public updateSafeUrlString() {
    if (this.fusionService.selectedFusionContent) {
      const m = this.fusionService.selectedFusionContent.metadata as FusionContentYoutube;

      const params = new URLSearchParams();
      if (m.pv_cc_load_policy != 'default') params.set("cc_load_policy", "" + m.pv_cc_load_policy);
      if (m.pv_end != 'default') params.set("end", "" + m.pv_end);
      if (m.pv_start != 'default') params.set("start", "" + m.pv_start);

      params.set("autoplay", "" + m.pv_autoplay);
      params.set("color", "" + m.pv_color);
      params.set("controls", "" + m.pv_controls);
      params.set("disablekb", "" + m.pv_disablekb);
      params.set("fs", "" + m.pv_fs);
      params.set("iv_load_policy", "" + m.pv_iv_load_policy);
      params.set("loop", "" + m.pv_loop);
      params.set("rel", "" + m.pv_rel);

      const url = `https://www.youtube.com/embed/${m.videoId}?${params}`;
      this.safeHtml = this.sanitizer.bypassSecurityTrustResourceUrl(url);
    }
    return "";
  }

  public async addYoutube(youtubeUrl: string) {
    const videoId = this.getYoutubeUrlId(youtubeUrl);

    if (!videoId) {
      this.toastr.error("ERROR_400", ($localize`:@@invalid-domain:URL ist ungültig`));
      return;
    }

    await this.fusionService.updateFusionField('videoId', videoId);
  }

  protected setCustomCompletions(): customCode[] {
    const customCode: customCode[] = [];
    customCode.push({ code: 'YOUTUBE.YT', info: 'Gibt das YT Objekt zurück' });
    customCode.push({ code: 'YOUTUBE.currentPlayPosition', info: 'Aktuelle Position im Video' });
    customCode.push({ code: 'YOUTUBE.currentState', info: 'Aktueller Video-Status' });
    customCode.push({ code: 'YOUTUBE.lastEvent', info: 'Rückgabe des letzten Events' });
    customCode.push({ code: 'YOUTUBE.lastPlayPosition', info: 'Letzte bekannte Position im Video' });
    customCode.push({ code: 'YOUTUBE.lastState', info: 'Zuletzt bekannter Video-Status' });
    customCode.push({ code: 'YOUTUBE.player', info: 'Rückgabe des Video-Players' });
    return customCode;
  }
}
